// src/graph/documents.ts
// Centralisation de tous les documents GraphQL (queries, mutations, subscriptions)
// Ce fichier est la source unique pour toutes les requêtes GraphQL
// Utilisé à la fois dans l'application et dans les tests

import { gql } from '@apollo/client';

// ============================================================================
// QUERIES
// ============================================================================

/**
 * Liste tous les projets de l'utilisateur
 */
export const LIST_PROJECTS = gql`
	query ListProjects {
		listProjects {
			items {
				id
				reference
				name
				description
				createdAt
				type
			}
		}
	}
`;

/**
 * Récupère un projet complet par son ID
 */
export const GET_PROJECT = gql`
	query GetProject($id: ID!) {
		getProject(id: $id) {
			baselineScenarioDescription
			baselineScenarioImpacts
			analysisSummary
			indicators {
				createdAt
				description
				evaluatedAt
				frequency
				id
				indicatorCategoryId
				name
				reference
				scenarios {
					id
					scenarioId
					trend
					enabled
				}
				taggings {
					createdAt
					id
					objectId
					tagId
					type
				}
				trend
				weight
				enabled
			}
			createdAt
			description
			descriptionLong
			fishboneBranchs {
				createdAt
				description
				endOn
				evaluatedAt
				icon
				id
				items {
					completion
					createdAt
					description
					endOn
					evaluatedAt
					fishboneBranchId
					icon
					id
					name
					reference
					responsible
					scenarios {
						completion
						id
						scenarioId
						trend
						enabled
					}
					startOn
					trend
					updatedAt
					weight
					enabled
				}
				name
				reference
				responsible
				startOn
				updatedAt
				weight
				scenarios {
					id
					scenarioId
					enabled
				}
				enabled
			}
			fishboneDescription
			fishboneEndOn
			fishboneStartOn
			id
			impacts {
				id
				name
			}
			stakes {
				createdAt
				description
				evaluatedAt
				id
				name
				reference
				scenarios {
					id
					scenarioId
					trend
					enabled
				}
				taggings {
					createdAt
					id
					objectId
					tagId
					type
				}
				trend
				weight
				enabled
			}
			likelihoods {
				id
				name
			}
			name
			reference
			responsible
			riskCategories {
				createdAt
				description
				id
				name
				weight
			}
			risks {
				createdAt
				description
				evaluatedAt
				id
				impact
				impactedStakes {
					createdAt
					id
					stakeId
					riskId
					strength
				}
				isOpportunity
				likelihood
				name
				reference
				riskCategoryId
				scenarios {
					id
					impact
					likelihood
					scenarioId
					enabled
				}
				taggings {
					createdAt
					id
					objectId
					tagId
					type
				}
				enabled
			}
			scenarios {
				createdAt
				description
				id
				impacts
				name
				enabled
			}
			stakeholders {
				createdAt
				description
				icon
				id
				impact
				impactedIndicators {
					indicatorId
					id
					stakeholderId
					strength
				}
				impactedStakes {
					id
					stakeId
					stakeholderId
					strength
				}
				impactedRisks {
					id
					riskId
					stakeholderId
					strength
				}
				links {
					id
					originId
					strength
					targetId
				}
				name
				scenarios {
					id
					impact
					scenarioId
					trend
					x
					y
					enabled
				}
				taggings {
					id
					objectId
					tagId
					type
				}
				trend
				x
				y
				enabled
			}
			tags {
				createdAt
				description
				id
				name
			}
			type
		}
	}
`;

/**
 * Récupère les commentaires d'un objet
 */
export const GET_COMMENTS = gql`
	query GetComments($objectType: String!, $objectId: String!) {
		getComments(objectType: $objectType, objectId: $objectId) {
			id
			description
			createdAt
			objectId
			objectType
			commentedAt
		}
	}
`;

/**
 * Query pour l'assistant IA
 */
export const ASK_BOT = gql`
	query askBot(
		$projectId: String
		$question: String!
		$threadId: String
		$scenarioId: String
		$screen: String
	) {
		askBot(
			projectId: $projectId
			question: $question
			threadId: $threadId
			scenarioId: $scenarioId
			screen: $screen
		)
	}
`;

export const PREVIEW_AI_QUERY = gql`
	query PreviewAiQuery($name: String!, $mission: String, $description: String, $context: String, $language: String) {
		previewAiQuery(name: $name, mission: $mission, description: $description, context: $context, language: $language)
	}
`;

/**
 * MCP access info (token info, endpoint, last used) for the current user.
 * Le token complet n'est jamais retourné par cette query — uniquement par
 * generateMcpToken / regenerateMcpToken au moment de la création.
 */
export const GET_MCP_ACCESS = gql`
	query GetMcpAccess {
		getMcpAccess {
			endpoint
			info {
				prefix
				name
				createdAt
				lastUsedAt
				revokedAt
			}
		}
	}
`;

export const GENERATE_MCP_TOKEN = gql`
	mutation GenerateMcpToken($name: String) {
		generateMcpToken(name: $name) {
			endpoint
			token
			info {
				prefix
				name
				createdAt
				lastUsedAt
				revokedAt
			}
		}
	}
`;

export const REVOKE_MCP_TOKEN = gql`
	mutation RevokeMcpToken {
		revokeMcpToken {
			endpoint
			info {
				prefix
				name
				createdAt
				lastUsedAt
				revokedAt
			}
		}
	}
`;

export const REGENERATE_MCP_TOKEN = gql`
	mutation RegenerateMcpToken($name: String) {
		regenerateMcpToken(name: $name) {
			endpoint
			token
			info {
				prefix
				name
				createdAt
				lastUsedAt
				revokedAt
			}
		}
	}
`;

// ============================================================================
// ATTACHMENTS — private project documents fed to AI analysis
// ============================================================================

export const LIST_PROJECT_ATTACHMENTS = gql`
	query ListProjectAttachments($projectId: String, $tempSessionId: String) {
		listProjectAttachments(projectId: $projectId, tempSessionId: $tempSessionId) {
			id
			projectId
			tempSessionId
			filename
			size
			contentType
			status
			uploadedAt
			scanError
		}
	}
`;

export const REQUEST_ATTACHMENT_UPLOAD = gql`
	mutation RequestAttachmentUpload(
		$projectId: String
		$tempSessionId: String
		$filename: String!
		$size: Float!
		$contentType: String!
	) {
		requestAttachmentUpload(
			projectId: $projectId
			tempSessionId: $tempSessionId
			filename: $filename
			size: $size
			contentType: $contentType
		)
	}
`;

export const DELETE_ATTACHMENT = gql`
	mutation DeleteAttachment($attachmentId: ID!) {
		deleteAttachment(attachmentId: $attachmentId)
	}
`;

export const CLAIM_ATTACHMENTS = gql`
	mutation ClaimAttachments($projectId: String!, $tempSessionId: String!) {
		claimAttachments(projectId: $projectId, tempSessionId: $tempSessionId)
	}
`;

export const SUBSCRIBE_TO_ATTACHMENT_STATUS = gql`
	subscription OnAttachmentStatusChanged($projectId: String, $tempSessionId: String) {
		onAttachmentStatusChanged(projectId: $projectId, tempSessionId: $tempSessionId) {
			id
			projectId
			tempSessionId
			filename
			size
			contentType
			status
			uploadedAt
			scanError
		}
	}
`;

// ============================================================================
// MUTATIONS - PROJECT
// ============================================================================

export const CREATE_PROJECT = gql`
	mutation CreateProject(
		$name: String!
		$description: String
		$type: String
	) {
		createProject(name: $name, description: $description, type: $type) {
			id
		}
	}
`;

export const UPDATE_PROJECT = gql`
	mutation UpdateProject($id: ID!, $name: String, $description: String) {
		updateProject(id: $id, name: $name, description: $description)
	}
`;

export const DELETE_PROJECT = gql`
	mutation DeleteProject($id: ID!) {
		deleteProject(id: $id)
	}
`;

// ============================================================================
// MUTATIONS - STAKEHOLDER
// ============================================================================

export const CREATE_STAKEHOLDER = gql`
	mutation CreateStakeholder(
		$projectId: String!
		$name: String!
		$description: String
		$icon: String
		$x: Float
		$y: Float
	) {
		createStakeholder(
			projectId: $projectId
			name: $name
			description: $description
			icon: $icon
			x: $x
			y: $y
		) {
			id
			name
			x
			y
		}
	}
`;

export const UPDATE_STAKEHOLDER = gql`
	mutation UpdateStakeholder(
		$id: ID!
		$name: String
		$description: String
		$icon: String
		$impact: String
		$trend: String
		$x: Float
		$y: Float
		$enabled: Boolean
	) {
		updateStakeholder(
			id: $id
			name: $name
			description: $description
			icon: $icon
			impact: $impact
			trend: $trend
			x: $x
			y: $y
			enabled: $enabled
		)
	}
`;

export const DELETE_STAKEHOLDER = gql`
	mutation DeleteStakeholder($id: ID!) {
		deleteStakeholder(id: $id)
	}
`;

// ============================================================================
// MUTATIONS - RISK
// ============================================================================

export const CREATE_RISK = gql`
	mutation CreateRisk(
		$projectId: String!
		$name: String!
		$description: String
		$likelihood: Float
		$impact: Float
		$isOpportunity: Boolean
	) {
		createRisk(
			projectId: $projectId
			name: $name
			description: $description
			likelihood: $likelihood
			impact: $impact
			isOpportunity: $isOpportunity
		) {
			id
		}
	}
`;

export const UPDATE_RISK = gql`
	mutation UpdateRisk(
		$id: ID!
		$name: String
		$description: String
		$likelihood: Float
		$impact: Float
		$riskCategoryId: String
		$enabled: Boolean
	) {
		updateRisk(
			id: $id
			name: $name
			description: $description
			likelihood: $likelihood
			impact: $impact
			riskCategoryId: $riskCategoryId
			enabled: $enabled
		)
	}
`;

export const DELETE_RISK = gql`
	mutation DeleteRisk($id: ID!) {
		deleteRisk(id: $id)
	}
`;

// ============================================================================
// MUTATIONS - STAKE
// ============================================================================

export const CREATE_STAKE = gql`
	mutation CreateStake(
		$projectId: String!
		$name: String!
		$description: String
		$weight: Float
		$trend: String
		$enabled: Boolean
	) {
		createStake(
			projectId: $projectId
			name: $name
			description: $description
			weight: $weight
			trend: $trend
			enabled: $enabled
		) {
			id
		}
	}
`;

export const UPDATE_STAKE = gql`
	mutation UpdateStake(
		$id: ID!
		$name: String
		$description: String
		$weight: Float
		$trend: String
		$enabled: Boolean
	) {
		updateStake(
			id: $id
			name: $name
			description: $description
			weight: $weight
			trend: $trend
			enabled: $enabled
		)
	}
`;

export const DELETE_STAKE = gql`
	mutation DeleteStake($id: ID!) {
		deleteStake(id: $id)
	}
`;

// ============================================================================
// MUTATIONS - CONSTRAINT
// ============================================================================

export const CREATE_CONSTRAINT = gql`
	mutation CreateIndicator(
		$projectId: String!
		$name: String!
		$description: String
		$weight: Float
		$trend: String
		$enabled: Boolean
	) {
		createIndicator(
			projectId: $projectId
			name: $name
			description: $description
			weight: $weight
			trend: $trend
			enabled: $enabled
		) {
			id
		}
	}
`;

export const UPDATE_CONSTRAINT = gql`
	mutation UpdateIndicator(
		$id: ID!
		$name: String
		$description: String
		$reference: String
		$weight: Float
		$trend: String
		$enabled: Boolean
		$frequency: String
		$evaluatedAt: String
		$indicatorCategoryId: ID
	) {
		updateIndicator(
			id: $id
			name: $name
			description: $description
			reference: $reference
			weight: $weight
			trend: $trend
			enabled: $enabled
			frequency: $frequency
			evaluatedAt: $evaluatedAt
			indicatorCategoryId: $indicatorCategoryId
		)
	}
`;

export const DELETE_CONSTRAINT = gql`
	mutation DeleteIndicator($id: ID!) {
		deleteIndicator(id: $id)
	}
`;

// ============================================================================
// MUTATIONS - SCENARIO
// ============================================================================

export const CREATE_SCENARIO = gql`
	mutation CreateScenario(
		$projectId: String!
		$name: String!
		$description: String
		$impacts: String
		$referenceScenarioId: String
	) {
		createScenario(
			projectId: $projectId
			name: $name
			description: $description
			impacts: $impacts
			referenceScenarioId: $referenceScenarioId
		)
	}
`;

export const UPDATE_SCENARIO = gql`
	mutation UpdateScenario(
		$id: ID!
		$name: String
		$description: String
		$impacts: String
		$enabled: Boolean
	) {
		updateScenario(
			id: $id
			name: $name
			description: $description
			impacts: $impacts
			enabled: $enabled
		)
	}
`;

export const DELETE_SCENARIO = gql`
	mutation DeleteScenario($id: ID!) {
		deleteScenario(id: $id)
	}
`;

// ============================================================================
// MUTATIONS - COMMENT
// ============================================================================

export const CREATE_COMMENT = gql`
	mutation CreateComment(
		$projectId: String!
		$objectType: String!
		$objectId: String!
		$description: String!
	) {
		createComment(
			projectId: $projectId
			objectType: $objectType
			objectId: $objectId
			description: $description
		) {
			id
		}
	}
`;

export const DELETE_COMMENT = gql`
	mutation DeleteComment($id: ID!) {
		deleteComment(id: $id)
	}
`;

// ============================================================================
// SUBSCRIPTIONS
// ============================================================================

/**
 * Subscription pour les changements en temps réel sur un projet
 */
export const SUBSCRIBE_TO_PROJECT_CHANGES = gql`
	subscription onProjectChange($projectId: String!) {
		onProjectChange(projectId: $projectId) {
			projectId
			objectId
			type
			operation
			changes
			author
		}
	}
`;

/**
 * Subscription pour les messages du projet (IA, etc.)
 */
export const SUBSCRIBE_TO_MESSAGES = gql`
	subscription subscribeToMessages($projectId: String!) {
		onProjectMessage(projectId: $projectId) {
			projectId
			message
		}
	}
`;

// ============================================================================
// DOCUMENT REGISTRY - Pour faciliter le mocking
// ============================================================================

/**
 * Registre de tous les documents GraphQL
 * Utile pour le mocking et la référence
 */
export const DOCUMENTS = {
	// Queries
	LIST_PROJECTS,
	GET_PROJECT,
	GET_COMMENTS,
	ASK_BOT,
	// Mutations - Project
	CREATE_PROJECT,
	UPDATE_PROJECT,
	DELETE_PROJECT,
	// Mutations - Stakeholder
	CREATE_STAKEHOLDER,
	UPDATE_STAKEHOLDER,
	DELETE_STAKEHOLDER,
	// Mutations - Risk
	CREATE_RISK,
	UPDATE_RISK,
	DELETE_RISK,
	// Mutations - Stake
	CREATE_STAKE,
	UPDATE_STAKE,
	DELETE_STAKE,
	// Mutations - Indicator
	CREATE_CONSTRAINT,
	UPDATE_CONSTRAINT,
	DELETE_CONSTRAINT,
	// Mutations - Scenario
	CREATE_SCENARIO,
	UPDATE_SCENARIO,
	DELETE_SCENARIO,
	// Mutations - Comment
	CREATE_COMMENT,
	DELETE_COMMENT,
	// Subscriptions
	SUBSCRIBE_TO_PROJECT_CHANGES,
	SUBSCRIBE_TO_MESSAGES
} as const;

export type DocumentName = keyof typeof DOCUMENTS;
